// KITEIQ unit system helpers.
// Canonical storage is always METRIC: weight in kg, wind in knots, temp in °C.
// The UI converts to the user's preferred system (metric | imperial) for display/input.

export const UNIT_SYSTEMS = ['metric', 'imperial'];

// KITEIQ is a Dutch/European-first kitesurf app, so the default unit system is METRIC.
// Imperial is only used when the rider explicitly selects it (persisted in `kiteiq_units`).
export function detectUnits() {
  return 'metric';
}

// Resolve the active unit system for the current user (auth user -> localStorage -> locale).
export function resolveUnits(user) {
  if (user?.units && UNIT_SYSTEMS.includes(user.units)) return user.units;
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('kiteiq_units') : null;
  if (stored && UNIT_SYSTEMS.includes(stored)) return stored;
  return detectUnits();
}

// ---------------- conversions ----------------
export const kgToLbs = (kg) => kg * 2.2046226218;
export const lbsToKg = (lb) => lb / 2.2046226218;
export const knotsToMph = (kn) => kn * 1.150779448;
export const mphToKnots = (mph) => mph / 1.150779448;
export const cToF = (c) => (c * 9) / 5 + 32;
export const fToC = (f) => ((f - 32) * 5) / 9;

// ---------------- unit labels ----------------
export const weightUnit = (u) => (u === 'imperial' ? 'lbs' : 'kg');
export const windUnit = (u) => (u === 'imperial' ? 'mph' : 'kn');
export const tempUnit = (u) => (u === 'imperial' ? '°F' : '°C');

// ---------------- canonical -> display value ----------------
export function displayWeight(kg, u) {
  if (kg == null || kg === '') return null;
  return Math.round(u === 'imperial' ? kgToLbs(kg) : kg);
}
export function displayWind(kn, u, digits = 0) {
  if (kn == null || kn === '') return null;
  const v = u === 'imperial' ? knotsToMph(kn) : kn;
  return Number(v.toFixed(digits));
}
export function displayTemp(c, u) {
  if (c == null || c === '') return null;
  return Math.round(u === 'imperial' ? cToF(c) : c);
}

// ---------------- display input -> canonical ----------------
export function weightToKg(val, u) {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return null;
  return u === 'imperial' ? lbsToKg(n) : n;
}
export function windToKnots(val, u) {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return null;
  return u === 'imperial' ? mphToKnots(n) : n;
}
export function tempToC(val, u) {
  const n = parseFloat(val);
  if (Number.isNaN(n)) return null;
  return u === 'imperial' ? fToC(n) : n;
}

// ---------------- formatted strings ----------------
export function fmtWeight(kg, u) {
  const v = displayWeight(kg, u);
  return v == null ? '—' : `${v} ${weightUnit(u)}`;
}
export function fmtWind(kn, u, digits = 0) {
  const v = displayWind(kn, u, digits);
  return v == null ? '—' : `${v} ${windUnit(u)}`;
}
export function fmtTemp(c, u) {
  const v = displayTemp(c, u);
  return v == null ? '—' : `${v}${tempUnit(u)}`;
}

// Sensible default placeholders per unit system.
export const weightPlaceholder = (u) => (u === 'imperial' ? 'e.g. 175' : 'e.g. 78');
export const windPlaceholder = (u) => (u === 'imperial' ? 'e.g. 21' : 'e.g. 18');
export const tempPlaceholder = (u) => (u === 'imperial' ? 'e.g. 61' : 'e.g. 16');

// ---------------- Strava activity helpers (canonical: meters, m/s, seconds) ----------------
export const msToKnots = (ms) => ms * 1.9438444924;
export const msToMph = (ms) => ms * 2.2369362921;
export const distanceUnit = (u) => (u === 'imperial' ? 'mi' : 'km');
export const elevUnit = (u) => (u === 'imperial' ? 'ft' : 'm');

// speed (m/s) -> rider unit (knots metric / mph imperial)
export function fmtSpeed(ms, u, digits = 1) {
  if (ms == null) return '—';
  const v = u === 'imperial' ? msToMph(ms) : msToKnots(ms);
  return `${v.toFixed(digits)} ${windUnit(u)}`;
}
// distance (m) -> km / mi
export function fmtDistance(m, u, digits = 1) {
  if (m == null) return '—';
  const v = u === 'imperial' ? m / 1609.344 : m / 1000;
  return `${v.toFixed(digits)} ${distanceUnit(u)}`;
}
// elevation gain (m) -> m / ft
export function fmtElev(m, u) {
  if (m == null) return '—';
  const v = u === 'imperial' ? Math.round(m * 3.28084) : Math.round(m);
  return `${v} ${elevUnit(u)}`;
}
// duration in seconds -> "1h 23m" / "45m 10s"
export function fmtDuration(seconds) {
  if (seconds == null) return '—';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

// ---------------- board dimensions ----------------
export const cmToInches = (cm) => cm / 2.54;
export const lengthUnit = (u) => (u === 'imperial' ? 'in' : 'cm');

// Format a board's length x width in the rider's unit system.
// Falls back to the model-provided `size` string when explicit cm are missing.
export function fmtBoardDims(board, u) {
  if (!board) return null;
  const L = board.length_cm;
  const W = board.width_cm;
  if (L != null && W != null && !Number.isNaN(L) && !Number.isNaN(W)) {
    if (u === 'imperial') {
      return `${cmToInches(L).toFixed(1)} × ${cmToInches(W).toFixed(1)} in`;
    }
    return `${Math.round(L)} × ${Math.round(W)} cm`;
  }
  return board.size || null;
}

